import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import ComicCard from "@/components/common/ComicCard";
import SectionHeading from "@/components/common/SectionHeading";
import { categories, comics } from "@/mocks/comics";

const featured = comics.slice(0, 4);
const highRated = [...comics].filter((c) => c.category === "high-rated").sort((a, b) => b.rating - a.rating);
const hiddenGems = comics.filter((c) => c.category === "hidden-gems");
const checklistComics = comics.filter((c) => c.category === "checklist");

const faqs = [
  {
    q: "漫荒是什么？为什么选择我爱漫画网？",
    a: "漫荒指想看漫画却不知道该看什么的时刻。我爱漫画网通过补番清单、高分漫画与冷门佳作等栏目，帮你按口碑和类型快速锁定下一本好漫画。",
  },
  {
    q: "榜单上的评分是怎么来的？",
    a: "评分综合了国内外主要漫画平台的口碑数据与读者长期反馈，仅作参考，最终是否好看还是要看你的个人口味。",
  },
  {
    q: "补番清单多久更新一次？",
    a: "我们每周整理并更新一期补番清单，按主题、类型或热门程度打包推荐，方便你整段安排阅读计划。",
  },
  {
    q: "可以投稿推荐自己喜欢的漫画吗？",
    a: "当然可以。欢迎通过「联系我们」页面投稿，我们会审核后将其补充进对应的栏目与榜单。",
  },
];

export default function Home() {
  useSeo({
    title: "我爱漫画网 | 漫荒推荐 · 补番清单 · 高分漫画冷门佳作",
    description:
      "漫荒怎么办？我爱漫画网为你收录高分漫画、冷门佳作与必看榜单，提供口碑漫画推荐与实用补番清单，帮你快速找到好漫画，告别剧荒。",
    path: "/",
  });

  return (
    <>
      {/* 首屏 Hero */}
      <section className="relative w-full overflow-hidden bg-foreground-950 text-background-50">
        <img
          src="https://readdy.ai/api/search-image?query=A%20stylized%20editorial%20illustration%20collage%20of%20manga%20reading%20culture%2C%20open%20manga%20pages%20and%20floating%20colorful%20speech%20bubbles%20with%20dynamic%20ink%20brush%20strokes%2C%20warm%20cream%20paper%20background%20with%20vermillion%20red%20and%20amber%20accents%2C%20artistic%20magazine%20cover%20aesthetic%2C%20rich%20texture%2C%20high%20detail&width=1600&height=900&seq=home-hero&orientation=landscape"
          alt="我爱漫画网首屏插画：漫画阅读与补番推荐"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/80 via-foreground-950/50 to-background-50" />
        <div className="container-page relative z-10 flex w-full flex-col items-center justify-center py-16 text-center md:py-24">
          <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-background-50/30 bg-foreground-950/40 px-4 py-1.5 text-sm text-background-50 backdrop-blur-sm">
            <i className="ri-sparkling-2-line text-accent-400" />
            每周更新的补番清单与漫画榜单
          </span>
          <h1 className="font-heading text-3xl font-bold leading-tight md:text-5xl">
            告别漫荒
            <br className="md:hidden" />
            找到下一本<span className="text-accent-400">好漫画</span>
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-100 md:text-base">
            高分漫画、冷门佳作、必看榜单与实用补番清单全收录。口碑漫画推荐，帮你快速找到好漫画，从此再也不怕"剧荒"。
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/rankings"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              去逛推荐榜单
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/category/hidden-gems"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-medium text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
            >
              发现冷门佳作
            </Link>
          </div>
          <dl className="mt-10 grid w-full max-w-2xl grid-cols-2 gap-px overflow-hidden rounded-lg border border-background-50/20 bg-background-50/10 backdrop-blur-sm sm:grid-cols-4">
            {[
              { n: "4", t: "推荐栏目" },
              { n: "120+", t: "精选作品" },
              { n: "9.2+", t: "平均评分" },
              { n: "每周", t: "更新清单" },
            ].map((s) => (
              <div key={s.t} className="flex flex-col items-center gap-1 bg-foreground-950/40 px-3 py-4">
                <dt className="sr-only">{s.t}</dt>
                <dd className="font-heading text-xl font-bold text-accent-400 md:text-2xl">{s.n}</dd>
                <dd className="text-xs text-background-100">{s.t}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* 栏目概览 */}
      <section className="container-page section-pad">
        <SectionHeading
          kicker="栏目导航"
          title="按栏目找漫画"
          description="无论你是老读者还是刚入坑，都能从下面四个栏目快速找到想看的好漫画。"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="group flex flex-col rounded-lg border border-background-200/80 bg-background-100 p-5 transition-colors hover:border-primary-300 md:p-6"
            >
              <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-xl text-secondary-700 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                <i className={c.icon} />
              </span>
              <h3 className="font-heading text-lg font-bold text-foreground-950 group-hover:text-primary-700">
                {c.name}
              </h3>
              <p className="mt-1 text-xs font-medium text-accent-600">{c.subtitle}</p>
              <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-600">{c.description}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
                去看看
                <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* 本周人气补番 */}
      <section className="bg-background-100">
        <div className="container-page section-pad">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <SectionHeading
              kicker="本周人气"
              title="正在被补的热门作品"
              description="编辑部从各栏目中精选的近期人气之作，闭眼入也不踩雷。"
            />
            <Link
              to="/rankings"
              className="mb-6 inline-flex shrink-0 items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 md:mb-8"
            >
              查看全部榜单
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 md:gap-5">
            {featured.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </div>
      </section>

      {/* 高分漫画 Top 榜单 */}
      <section className="container-page section-pad">
        <SectionHeading
          kicker="高分漫画"
          title="口碑封神的高分榜单"
          description="评分长期高居前列、剧情与作画都经得起推敲的高分漫画精选。"
        />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 md:gap-5">
          {highRated.map((comic, i) => (
            <Link
              key={comic.id}
              to={`/comic/${comic.id}`}
              className="group flex items-center gap-4 rounded-lg border border-background-200/80 bg-background-100 p-4 transition-colors hover:border-primary-300"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-primary-50 font-heading text-xl font-bold text-primary-600">
                {i + 1}
              </span>
              <div className="relative h-24 w-16 shrink-0 overflow-hidden rounded-md sm:h-28 sm:w-20">
                <img
                  src={comic.cover}
                  alt={comic.coverAlt}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="truncate font-heading text-base font-bold text-foreground-950 group-hover:text-primary-700">
                    {comic.title}
                  </h3>
                  <span className="shrink-0 rounded-md bg-accent-100 px-1.5 py-0.5 text-xs font-bold text-accent-700">
                    {comic.rating}
                  </span>
                </div>
                <p className="mt-1 text-xs text-foreground-500">{comic.author} · {comic.status}</p>
                <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600 sm:text-sm">
                  {comic.reason}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 冷门佳作 */}
      <section className="bg-foreground-950 text-background-50">
        <div className="container-page section-pad">
          <SectionHeading
            kicker="冷门佳作"
            title="宝藏藏在这些地方"
            description="热度不高但质量惊人的冷门佳作，专治口味刁钻的老读者。"
          />
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 md:gap-6">
            <div className="relative flex min-h-[360px] flex-col justify-end overflow-hidden rounded-lg p-6 md:min-h-[420px] md:p-8">
              <img
                src={hiddenGems[0].cover}
                alt={hiddenGems[0].coverAlt}
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/30 to-transparent" />
              <div className="relative z-10">
                <span className="mb-2 inline-block rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-foreground-950">
                  本期编辑推荐
                </span>
                <h3 className="font-heading text-2xl font-bold md:text-3xl">{hiddenGems[0].title}</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-background-100">{hiddenGems[0].reason}</p>
                <Link
                  to={`/comic/${hiddenGems[0].id}`}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看详情
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:gap-6">
              {hiddenGems.slice(1, 3).map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group flex flex-col overflow-hidden rounded-lg border border-foreground-800/60 bg-foreground-900/40"
                >
                  <div className="relative aspect-[4/3] w-full overflow-hidden">
                    <img
                      src={comic.cover}
                      alt={comic.coverAlt}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-1.5 p-4">
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading text-base font-bold">{comic.title}</h3>
                      <span className="rounded-md bg-accent-500 px-1.5 py-0.5 text-xs font-bold text-foreground-950">
                        {comic.rating}
                      </span>
                    </div>
                    <p className="line-clamp-2 text-xs leading-relaxed text-background-200">{comic.synopsis}</p>
                  </div>
                </Link>
              ))}
              <Link
                to="/category/hidden-gems"
                className="flex flex-col items-start justify-between gap-4 rounded-lg border border-dashed border-foreground-700 bg-transparent p-5 transition-colors hover:border-accent-500 hover:bg-foreground-900/40"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-500 text-xl text-foreground-950">
                  <i className="ri-sparkling-line" />
                </span>
                <div>
                  <h3 className="font-heading text-lg font-bold">更多冷门宝藏</h3>
                  <p className="mt-2 text-sm text-background-200">还有 {hiddenGems.length - 1} 部被低估的佳作，等你来挖。</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-accent-400">
                    去看看
                    <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 补番清单 */}
      <section className="container-page section-pad">
        <SectionHeading
          kicker="补番清单"
          title="按主题打包的补番清单"
          description="整理成册的补番清单，按主题打包推荐，一次看个够。"
        />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 md:gap-5">
          {checklistComics.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </section>

      {/* 为什么相信我们 */}
      <section className="bg-background-100">
        <div className="container-page section-pad">
          <SectionHeading
            kicker="我们的口碑"
            title="为什么把补番交给这里"
            align="center"
          />
          <div className="mx-auto grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-3 md:gap-5">
            {[
              {
                icon: "ri-draft-line",
                title: "编辑精挑细选",
                desc: "每部作品都经过编辑部逐本试读与口碑核查，只推荐真正值得花时间的好漫画。",
              },
              {
                icon: "ri-list-check-3",
                title: "清单随时更新",
                desc: "补番清单与榜单每周更新，紧跟热门与口碑变化，让你的收藏夹永远不荒。",
              },
              {
                icon: "ri-focus-3-line",
                title: "只讲阅读体验",
                desc: "不堆砌概念，从故事、人物与阅读体验出发，用大白话告诉你为什么值得看。",
              },
            ].map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200/80 bg-background-50 p-6 text-center">
                <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-2xl text-accent-700">
                  <i className={v.icon} />
                </span>
                <h3 className="font-heading text-base font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 常见问题预告 */}
      <section className="container-page section-pad">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 md:gap-6">
          <SectionHeading
            kicker="常见问题"
            title="你关心的问题"
            description="关于漫荒、评分、清单更新与投稿的常见疑问，这里都有答案。"
          />
          <ul className="divide-y divide-background-200">
            {faqs.map((f) => (
              <li key={f.q} className="py-4 first:pt-0 last:pb-0">
                <h3 className="flex items-start gap-2 text-sm font-bold text-foreground-950 md:text-base">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs text-primary-600">
                    <i className="ri-question-line" />
                  </span>
                  {f.q}
                </h3>
                <p className="mt-1.5 pl-7 text-sm leading-relaxed text-foreground-600">{f.a}</p>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-6 text-center">
          <Link to="/faq" className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700">
            查看更多常见问题
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="container-page pb-12 md:pb-16">
        <div className="relative overflow-hidden rounded-lg bg-primary-500 px-6 py-12 text-center md:px-10 md:py-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.18),transparent_45%)]" />
          <div className="relative z-10 mx-auto max-w-2xl">
            <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
              你也有想安利给全世界的好漫画？
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-primary-50 md:text-base">
              欢迎投稿推荐你心中的高分漫画与冷门佳作，让更多人告别剧荒，一起把好作品挖出来。
            </p>
            <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-background-50 px-6 py-3 text-sm font-bold text-primary-700 transition-colors hover:bg-background-100"
              >
                <i className="ri-mail-send-line" />
                投稿交流
              </Link>
              <Link
                to="/rankings"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-background-50/50 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                先逛逛榜单
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}